import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { sortDate } from '@skop-app/lib-domain/utils'

import {
  getCatalogData,
  getReducedProducts,
  normalizeCatalogLotHeaders,
  sanitizeCatalogBlocks,
  sanitizeHeaderBlockData,
  sortByRotorProductID,
} from '@/utils/catalog.handler'
import { extractValue, resolveLookupLabel } from '@/utils/product.handler'
import { matchKey } from '@/utils/matchKey.utils'
import {
  AdaptedProduct,
  Catalog,
  CatalogBlock,
  CatalogHeaderBlock,
  CatalogItem,
  CatalogProductBlock,
  Context,
} from '@/types'
import { buildSyntheticHeaderId } from '@/utils/catalog.syntheticHeaderId'
import { resolveCatalogLanguageSettings } from '@/utils/languagePolicy'
import { getAmplifyClient } from '@/services/amplifyClient'

import { catalogsByProjectQuery, getCatalogQuery } from '../graphql'

const client = getAmplifyClient()

const parseCatalogMetadataValue = (value: string | undefined): unknown => {
  if (!value) return undefined

  try {
    return JSON.parse(value) as unknown
  } catch {
    return undefined
  }
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

export const catalogVersionAdapter = (catalog: Catalog) => {
  const versionMeta = catalog.metadata?.find((m) => m.key === 'version')
  const createdAtMeta = catalog.metadata?.find((m) => m.key === 'createdAt')
  const supportedLocalesMeta = catalog.metadata?.find(
    (m) => m.key === 'supportedLocales',
  )
  const defaultLocaleMeta = catalog.metadata?.find(
    (m) => m.key === 'defaultLocale',
  )
  const firstPageNumberMeta = catalog.metadata?.find(
    (m) => m.key === 'firstPageNumber',
  )
  const parsedSupportedLocales = parseCatalogMetadataValue(
    supportedLocalesMeta?.value,
  )
  const supportedLocales = isStringArray(parsedSupportedLocales)
    ? parsedSupportedLocales
    : undefined
  const hasLanguageMetadata = Boolean(
    supportedLocalesMeta?.value || defaultLocaleMeta?.value,
  )
  const languageSettings = hasLanguageMetadata
    ? resolveCatalogLanguageSettings({
        supportedLocales,
        defaultLocale: defaultLocaleMeta?.value,
      })
    : undefined
  const firstPageNumber = firstPageNumberMeta?.value
    ? Number.parseInt(firstPageNumberMeta.value, 10)
    : undefined

  return {
    ...catalog,
    version: versionMeta ? Number.parseInt(versionMeta.value || '0', 10) : 0,
    createdAt: createdAtMeta?.value || catalog.lastUpdated,
    ...(languageSettings && {
      supportedLocales: languageSettings.supportedLocales,
      defaultLocale: languageSettings.defaultLocale,
    }),
    firstPageNumber,
  }
}

export const pruneEmptyCatalogSections = (
  blocks: CatalogBlock[],
): CatalogBlock[] => {
  const result: CatalogBlock[] = []
  let currentHeader: CatalogHeaderBlock | null = null
  let currentProducts: CatalogProductBlock[] = []

  const flush = () => {
    if (!currentHeader) return

    if (currentProducts.length > 0) {
      result.push(currentHeader, ...currentProducts)
    }

    currentHeader = null
    currentProducts = []
  }

  for (const block of blocks) {
    if (block.type === 'HEADER') {
      flush()
      currentHeader = block as CatalogHeaderBlock
      currentProducts = []
      continue
    }

    if (block.type === 'PRODUCT') {
      if (currentHeader) {
        currentProducts.push(block as CatalogProductBlock)
      } else {
        result.push(block)
      }
    }
  }

  flush()
  return result
}

const resolveCatalogSectionSort = (
  category: string | undefined,
  context?: Context,
) => {
  if (!category) {
    return {
      categoryIndex: Number.POSITIVE_INFINITY,
      lotNumber: Number.POSITIVE_INFINITY,
    }
  }

  const sanitized = sanitizeHeaderBlockData(
    { id: '__catalog-sort__', type: 'HEADER', category },
    context,
  )
  const match = matchKey(
    sanitized.category,
    context?.propertyOptions?.category ?? [],
    context?.optionsByID?.category,
  )
  const rawValue =
    match?.value?.toString() ?? match?.label ?? sanitized.category
  const categoryIndex = Number.parseFloat(
    rawValue.replace(',', '.').split('-')[0],
  )
  const resolvedIndex = Number.isFinite(categoryIndex)
    ? categoryIndex
    : Number.POSITIVE_INFINITY

  return {
    categoryIndex: resolvedIndex,
    lotNumber: Number.isFinite(resolvedIndex)
      ? Math.floor(resolvedIndex / 10) * 10
      : Number.POSITIVE_INFINITY,
  }
}

const findCatalogSectionInsertIndex = (
  blocks: CatalogBlock[],
  category: string,
  context?: Context,
) => {
  const incoming = resolveCatalogSectionSort(category, context)
  if (!Number.isFinite(incoming.categoryIndex)) return blocks.length

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index]
    if (block.type !== 'HEADER') continue

    const current = resolveCatalogSectionSort(block.data.category, context)
    if (
      Number.isFinite(current.lotNumber) &&
      current.lotNumber > incoming.lotNumber
    ) {
      return index
    }
    if (
      current.lotNumber === incoming.lotNumber &&
      current.categoryIndex > incoming.categoryIndex
    ) {
      return index
    }
  }

  return blocks.length
}

const collectCatalogSectionIndexes = (
  blocks: CatalogBlock[],
  context?: Context,
) => {
  const headerIndexByCategory = new Map<string, number>()
  const seenLots = new Set<string>()

  blocks.forEach((block, index) => {
    if (block.type !== 'HEADER') return

    const categoryKey = block.data.category
      ? sanitizeHeaderBlockData(block.data, context).category
      : undefined
    if (categoryKey && !headerIndexByCategory.has(categoryKey)) {
      headerIndexByCategory.set(categoryKey, index)
    }

    const lotKey = block.data.lot
      ? sanitizeHeaderBlockData(block.data, context).lot
      : undefined
    if (lotKey) {
      seenLots.add(lotKey)
    }
  })

  return {
    headerIndexByCategory,
    seenLots,
  }
}

export const useCatalogs = ({
  projectID,
}: {
  projectID: string | undefined
}): UseQueryResult<Catalog[]> => {
  return useQuery({
    queryKey: ['catalogs', projectID],
    queryFn: () => getCatalogsByProject({ projectID }),
    enabled: !!projectID,
    placeholderData: [],
  })
}

type CatalogsByProjectProps = {
  projectID: string | undefined
}

type CatalogSortDirection = 'ASC' | 'DESC'

type CatalogsByProjectPage = {
  items: Catalog[]
  nextToken?: string | null
}

const fetchCatalogsByProjectPage = async ({
  projectID,
  limit,
  nextToken,
  sortDirection,
}: CatalogsByProjectProps & {
  limit: number
  nextToken?: string | null
  sortDirection?: CatalogSortDirection
}) => {
  const result = (await client.graphql({
    query: catalogsByProjectQuery,
    variables: {
      projectCatalogsId: projectID,
      limit,
      nextToken,
      sortDirection,
    },
  })) as GraphQLResult<{ catalogsByProject: CatalogsByProjectPage }>

  const page = result.data?.catalogsByProject
  if (!page) throw new Error('Missing catalogsByProject data')
  return page
}

export const getCatalogsByProject = async ({
  projectID,
}: CatalogsByProjectProps): Promise<Catalog[]> => {
  const catalogs = await fetchCatalogsByProject({ projectID })
  return sortDate(
    catalogs.map(catalogVersionAdapter),
    'createdAt',
    'DESC',
  ) as Catalog[]
}

export const getLatestCatalogByProject = async ({
  projectID,
}: CatalogsByProjectProps): Promise<Catalog | undefined> => {
  try {
    if (!projectID) return undefined

    const page = await fetchCatalogsByProjectPage({
      projectID,
      limit: 1,
      sortDirection: 'DESC',
    })
    const latestCatalog = page.items?.[0]

    return latestCatalog ? catalogVersionAdapter(latestCatalog) : undefined
  } catch (e) {
    console.error(e)
    throw e
  }
}

const fetchCatalogsByProject = async ({
  projectID,
}: CatalogsByProjectProps) => {
  try {
    if (!projectID) return []

    const allItems: Catalog[] = []
    let nextToken: string | null | undefined

    do {
      const page = await fetchCatalogsByProjectPage({
        projectID,
        limit: 100,
        nextToken,
      })

      allItems.push(...(page.items ?? []))
      nextToken = page.nextToken
    } while (nextToken)

    return allItems
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const catalogAdapter = ({
  catalog,
  products,
  context,
  allowProductPlaceholders = false,
  isHydratingProducts = false,
}) => {
  try {
    // if catalogs are already created, return them updated with products
    if (catalog?.content) {
      const reducedProducts = getReducedProducts(products)
      const content = catalog.content?.[0]
      const updatedBlocks: CatalogBlock[] = []
      const reducedProductsByReferenceId = new Map(
        reducedProducts
          .filter((product) => typeof product.productID === 'string')
          .map((product) => [product.productID, product]),
      )
      const reducedProductsByLegacyId = new Map(
        reducedProducts
          .filter((product) => typeof product.id === 'string')
          .map((product) => [product.id, product]),
      )

      const getCatalogProductOverrides = (
        data: unknown,
      ): Partial<AdaptedProduct> => {
        if (typeof data !== 'object' || data === null) return {}

        const overrides: Partial<AdaptedProduct> = {}
        const record = data as Record<string, unknown>

        if ('detailedSheet' in record) {
          overrides.detailedSheet = record.detailedSheet
        }

        return overrides
      }

      const getCatalogProductSnapshot = (
        data: unknown,
      ): Record<string, unknown> | null =>
        typeof data === 'object' && data !== null
          ? (data as Record<string, unknown>)
          : null

      const canonicalizeCategory = (category?: string): string | undefined => {
        if (!category) return undefined
        return sanitizeHeaderBlockData(
          { id: '__catalog-category__', type: 'HEADER', category },
          context,
        ).category
      }

      const canonicalizeLot = (lot?: string): string | undefined => {
        if (!lot) return undefined
        return sanitizeHeaderBlockData(
          { id: '__catalog-lot__', type: 'HEADER', category: '', lot },
          context,
        ).lot
      }

      const firstProductByCategory = new Map<string, AdaptedProduct>()

      reducedProducts.forEach((product) => {
        const categoryKey = canonicalizeCategory(product.category?.value)
        if (categoryKey && !firstProductByCategory.has(categoryKey)) {
          firstProductByCategory.set(categoryKey, product)
        }
      })

      const resolveHeaderNodeValue = ({
        currentValue,
        fallbackField,
      }: {
        currentValue?: string
        fallbackField?: AdaptedProduct['category'] | AdaptedProduct['lot']
      }): string | undefined => {
        if (!currentValue && !fallbackField) return undefined

        const currentLabel = resolveLookupLabel({
          value: currentValue,
          references: context?.propertyOptions?.category,
          optionsByID: context?.optionsByID?.category,
          refData: context?.refDataByKey?.category,
        })

        if (currentLabel) {
          return currentValue
        }

        const fallbackLabel =
          typeof fallbackField?.label === 'string' &&
          fallbackField.label.trim().length > 0
            ? fallbackField.label
            : undefined
        if (fallbackLabel) return fallbackLabel

        const fallbackValue =
          typeof fallbackField?.value === 'string' &&
          fallbackField.value.trim().length > 0
            ? fallbackField.value
            : undefined
        if (fallbackValue) return fallbackValue

        return currentValue
      }

      const mergeDuplicateCategorySections = (
        blocks: CatalogBlock[],
      ): CatalogBlock[] => {
        type Section = {
          header: CatalogHeaderBlock
          products: CatalogProductBlock[]
        }

        // Build contiguous sections (header + products). If we ever encounter
        // products before any header (corrupted order), keep them as-is.
        const sections: Section[] = []
        const orphanProducts: CatalogProductBlock[] = []
        let current: Section | null = null
        for (const b of blocks) {
          if (b.type === 'HEADER') {
            if (current) sections.push(current)
            current = { header: b as CatalogHeaderBlock, products: [] }
          } else if (b.type === 'PRODUCT') {
            if (current) current.products.push(b as CatalogProductBlock)
            else orphanProducts.push(b as CatalogProductBlock)
          }
        }
        if (current) sections.push(current)

        const getCategoryKey = (header: CatalogHeaderBlock): string => {
          const sanitized = sanitizeHeaderBlockData(header.data, context)
          const value = sanitized.category
            ? canonicalizeCategory(sanitized.category)
            : undefined
          return value ?? ''
        }

        const mergedByCategory = new Map<string, Section>()
        const order: string[] = []
        const seenProductsByCategory = new Map<string, Set<string>>()

        const mergeHeaderData = (
          base: CatalogHeaderBlock,
          incoming: CatalogHeaderBlock,
        ): CatalogHeaderBlock => {
          const b = base.data
          const i = incoming.data

          const pickString = (a?: string, c?: string) =>
            a && a.trim().length ? a : c && c.trim().length ? c : (a ?? c)

          return {
            ...base,
            data: {
              ...b,
              // Prefer first non-empty values for description fields
              description: pickString(b.description, i.description) ?? '',
              descriptionEN: pickString(b.descriptionEN, i.descriptionEN),
              descriptionNL: pickString(b.descriptionNL, i.descriptionNL),
              // Keep lot if base doesn't have it but incoming does
              lot: b.lot ?? i.lot,
            },
          }
        }

        for (const sec of sections) {
          const key = getCategoryKey(sec.header)
          if (!key) continue

          if (!mergedByCategory.has(key)) {
            mergedByCategory.set(key, sec)
            order.push(key)
            seenProductsByCategory.set(
              key,
              new Set(sec.products.map((p) => p.id)),
            )
            continue
          }

          // Merge into first section
          const existing = mergedByCategory.get(key)!
          mergedByCategory.set(key, {
            header: mergeHeaderData(existing.header, sec.header),
            products: existing.products,
          })

          const seen = seenProductsByCategory.get(key) ?? new Set<string>()
          for (const p of sec.products) {
            if (seen.has(p.id)) continue
            existing.products.push(p)
            seen.add(p.id)
          }
          seenProductsByCategory.set(key, seen)
        }

        // Rebuild blocks in stable category order
        const result: CatalogBlock[] = [...orphanProducts]
        for (const key of order) {
          const sec = mergedByCategory.get(key)
          if (!sec) continue
          if (!sec.products.length) continue
          result.push(sec.header, ...sec.products)
        }
        return result
      }

      // Parse and update blocks from DB with project products
      content?.blocks?.map((block) => {
        try {
          const data: unknown = JSON.parse(block.data)
          // Parse existing headers and remove them if they don't have product anymore
          if (
            block.type === 'HEADER' &&
            typeof data === 'object' &&
            data !== null &&
            'category' in data &&
            typeof (data as { category?: unknown }).category === 'string'
          ) {
            const headerData = data as Record<string, unknown>
            const headerCategory = canonicalizeCategory(
              (data as { category: string }).category,
            )
            const matchedProduct = headerCategory
              ? firstProductByCategory.get(headerCategory)
              : undefined

            if (!matchedProduct && !allowProductPlaceholders) {
              return
            }

            updatedBlocks.push({
              ...block,
              data: {
                ...(headerData as Record<string, unknown>),
                id: block.id,
                category: resolveHeaderNodeValue({
                  currentValue:
                    typeof headerData.category === 'string'
                      ? headerData.category
                      : undefined,
                  fallbackField: matchedProduct?.category,
                }) as CatalogHeaderBlock['data']['category'],
                lot: resolveHeaderNodeValue({
                  currentValue:
                    typeof headerData.lot === 'string'
                      ? headerData.lot
                      : undefined,
                  fallbackField: matchedProduct?.lot,
                }),
                description:
                  typeof headerData.description === 'string'
                    ? headerData.description
                    : '',
              } as CatalogHeaderBlock['data'],
            })
          } else {
            // Parse existing products (remove or update)
            const referenceID =
              'referenceID' in block && typeof block.referenceID === 'string'
                ? block.referenceID
                : undefined

            const product =
              (referenceID
                ? reducedProductsByReferenceId.get(referenceID)
                : undefined) ??
              reducedProductsByReferenceId.get(block.id) ??
              reducedProductsByLegacyId.get(block.id)
            if (product) {
              updatedBlocks.push({
                ...block,
                data: {
                  ...product,
                  ...getCatalogProductOverrides(data),
                },
              })
            } else if (allowProductPlaceholders) {
              const productID = referenceID ?? block.id
              const snapshot = getCatalogProductSnapshot(data)
              updatedBlocks.push({
                ...block,
                data: {
                  id: block.id,
                  productID,
                  updatedBy: undefined,
                  __catalogHydrating: isHydratingProducts,
                  __catalogMissingProduct: !isHydratingProducts,
                  __catalogSnapshot: snapshot,
                  ...getCatalogProductOverrides(data),
                } as AdaptedProduct,
              })
            }
          }
        } catch (e) {
          console.error('Error parsing block data:', e, block)
        }
      })

      // Add new project products to the catalog
      const seenReferenceIds = new Set(
        updatedBlocks.flatMap((block) =>
          block.type === 'PRODUCT' && block.referenceID
            ? [block.referenceID]
            : [],
        ),
      )
      const seenLegacyIds = new Set(
        updatedBlocks.flatMap((block) =>
          block.type === 'PRODUCT' && block.id ? [block.id] : [],
        ),
      )
      // These indexes follow `updatedBlocks`; refresh after each inserted
      // section so later insertions use the current header positions and lots.
      let { headerIndexByCategory, seenLots } = collectCatalogSectionIndexes(
        updatedBlocks,
        context,
      )
      let hasAddedCatalogSection = false
      const refreshCatalogSectionIndexes = () => {
        const nextIndexes = collectCatalogSectionIndexes(updatedBlocks, context)
        headerIndexByCategory = nextIndexes.headerIndexByCategory
        seenLots = nextIndexes.seenLots
      }

      reducedProducts.forEach((product) => {
        const rawCategory = product.category?.value
        if (!rawCategory) return

        const productCategory = canonicalizeCategory(rawCategory) ?? rawCategory
        const rawLot = product.lot?.value
        const productLot = rawLot
          ? (canonicalizeLot(rawLot) ?? rawLot)
          : undefined

        // If this product is not already in the catalog, add it
        const existsByReferenceID = seenReferenceIds.has(product.productID)
        const existsByLegacyId = seenLegacyIds.has(product.id)

        if (!existsByReferenceID && !existsByLegacyId) {
          // Check if a header block for this product category already exists
          const existingHeaderIndex = headerIndexByCategory.get(productCategory)
          // If no header block exists for this product category, create one
          if (existingHeaderIndex === undefined) {
            // Check if a header block for this product lot already exists
            const isNewLot = !!productLot && !seenLots.has(productLot)
            // Create a new header block
            const headerId = buildSyntheticHeaderId({
              category: productCategory,
              lot: productLot,
            })
            const headerBlock: CatalogHeaderBlock = {
              id: headerId,
              type: 'HEADER',
              data: {
                id: headerId,
                type: 'HEADER',
                ...(isNewLot && productLot ? { lot: productLot } : {}),
                category: productCategory,
                description: '',
              },
            }
            // Insert the new section at its numeric category position without
            // re-sorting existing manually ordered sections.
            const headerInsertIndex = findCatalogSectionInsertIndex(
              updatedBlocks,
              productCategory,
              context,
            )
            updatedBlocks.splice(headerInsertIndex, 0, headerBlock)
            refreshCatalogSectionIndexes()
            hasAddedCatalogSection = true
            if (productLot) {
              seenLots.add(productLot)
            }
          }
          // Find the index of the header block for this product
          const insertIndex = headerIndexByCategory.get(productCategory) ?? -1
          // Create a new product block
          const productBlock: CatalogProductBlock = {
            id: product.id,
            type: 'PRODUCT',
            referenceID: product.productID,
            data: product,
          }
          // Add the new product block at the end of its category section
          if (insertIndex !== -1) {
            let endIndex = insertIndex + 1
            while (
              endIndex < updatedBlocks.length &&
              updatedBlocks[endIndex].type === 'PRODUCT'
            ) {
              endIndex += 1
            }
            updatedBlocks.splice(endIndex, 0, productBlock)
          }
          // Or at the very end of the catalog
          else updatedBlocks.push(productBlock)
          refreshCatalogSectionIndexes()

          seenReferenceIds.add(product.productID)
          seenLegacyIds.add(product.id)
        }
      })

      // Sanitize blocks to ensure proper UUIDs for category and lot
      const compactedBlocks = pruneEmptyCatalogSections(updatedBlocks)
      const sanitizedBlocks = sanitizeCatalogBlocks(compactedBlocks, context)
      const merged = mergeDuplicateCategorySections(sanitizedBlocks)

      return hasAddedCatalogSection
        ? normalizeCatalogLotHeaders(merged, context)
        : merged
    }

    // If no catalogs are created, create a new one with products
    const rawData = getCatalogData({
      products: sortByRotorProductID(products),
      context,
    })

    // Determine if data is an array of pages
    const isArrayOfPages =
      Array.isArray(rawData) && rawData.length > 0 && Array.isArray(rawData[0])

    // Flatten if it's array of pages, otherwise use as is
    const data: CatalogItem[] = isArrayOfPages
      ? rawData.flatMap((page) => page.items ?? page)
      : rawData

    // First, organize products by category and lot
    const categoriesMap: Record<
      string,
      {
        category: string
        lot?: string
        products: AdaptedProduct[]
      }
    > = {}

    // Group products by category
    data.forEach((item) => {
      // Skip items without category
      if (!item.category) return

      // Get category and lot values
      const categoryValue = extractValue(item.category)

      if (!categoryValue) return

      // Add this category to the map if it doesn't exist
      if (!categoriesMap[categoryValue]) {
        categoriesMap[categoryValue] = {
          category: extractValue(item.category),
          lot: extractValue(item.lot),
          products: [],
        }
      }

      // Add this product to the category
      if (item.id && 'updatedBy' in item) {
        categoriesMap[categoryValue].products.push(item)
      }
    })
    // Create blocks array with headers and products
    const blocks: CatalogBlock[] = []

    // For each category, add a header block followed by product blocks
    Object.values(categoriesMap).forEach(({ category, lot, products }) => {
      // Create header block
      const headerId = buildSyntheticHeaderId({ category, lot })
      const headerBlock: CatalogHeaderBlock = {
        id: headerId,
        type: 'HEADER',
        data: {
          id: headerId,
          type: 'HEADER',
          category,
          lot,
          description: '',
        },
      }

      // Add header block
      blocks.push(headerBlock)

      // Add product blocks
      products.forEach((product: AdaptedProduct) => {
        blocks.push({
          id: product.id ?? product.productID ?? uuid(),
          type: 'PRODUCT',
          referenceID: product.productID ?? '',
          data: product,
        })
      })
    })
    return blocks
  } catch (e) {
    console.error('Error in catalogAdapter:', e)
    throw e
  }
}

export const useCatalog = (id: string | undefined) => {
  return useQuery({
    queryKey: ['catalog', id],
    queryFn: () => getCatalog(id),
    enabled: !!id,
    select: (data) => catalogVersionAdapter(data),
  })
}

const getCatalog = async (id?: string) => {
  const {
    data: { getCatalog },
  } = (await client.graphql({
    query: getCatalogQuery,
    variables: { id },
  })) as GraphQLResult<{ getCatalog: Catalog }>

  return getCatalog
}
